import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Metric data structures
interface MetricPoint {
    timestamp: string;
    average?: number;
    maximum?: number;
    minimum?: number;
    count?: number;
}

interface MetricInfo {
    displayName?: string;
    unit?: string;
    data: MetricPoint[];
}

interface CategoryMetrics {
    [metricName: string]: MetricInfo;
}

interface AllMetrics {
    [category: string]: CategoryMetrics;
}

// Component props
interface MetricsDisplayProps {
    metrics: AllMetrics | undefined;
}

// MetricsDisplay component with proper TypeScript types
const MetricsDisplay: React.FC<MetricsDisplayProps> = ({ metrics }) => {
    const [activeCategory, setActiveCategory] = useState<string>('');
    const [selectedMetric, setSelectedMetric] = useState<string>('');

    // Get all available categories
    const categories = Object.keys(metrics || {}).filter(cat =>
        metrics?.[cat] && Object.keys(metrics[cat]).length > 0
    );

    useEffect(() => {
        if (categories.length > 0 && !activeCategory) {
            setActiveCategory(categories[0]);
        }
    }, [metrics, categories, activeCategory]);

   

    if (!metrics || Object.keys(metrics).length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <p className="text-gray-500">No metrics data available</p>
            </div>
        );
    }

    // Get available metrics for the current category
    const getAvailableMetrics = () => {
        if (!activeCategory || !metrics[activeCategory]) return [];

        return Object.keys(metrics[activeCategory]).map(key => {
            const metricInfo = metrics[activeCategory][key];
            return {
                value: key,
                label: key === 'undefined'
                    ? `${activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} (${metricInfo.unit})`
                    : metricInfo.displayName || key
            };
        });
    };

    // Get data for the selected metric (defaulting to 'undefined')
    const getMetricData = () => {
        if (!activeCategory || !metrics[activeCategory]) return [];

        // Default to 'undefined' as the metric key
        const metricKey = selectedMetric || 'undefined';
        const metricInfo = metrics[activeCategory][metricKey];

        if (!metricInfo || !metricInfo.data || metricInfo.data.length === 0) {
            return [];
        }

        return metricInfo.data.map(point => ({
            time: new Date(point.timestamp).toLocaleTimeString(),
            average: point.average !== undefined ? parseFloat(point.average.toFixed(2)) : null,
            maximum: point.maximum !== undefined ? parseFloat(point.maximum.toFixed(2)) : null,
            minimum: point.minimum !== undefined ? parseFloat(point.minimum.toFixed(2)) : null
        }));
    };

    // Get unit for the selected metric
    const getMetricUnit = (): string => {
        if (!selectedMetric || !metrics[activeCategory] || !metrics[activeCategory][selectedMetric]) {
            return '';
        }
        return metrics[activeCategory][selectedMetric].unit || '';
    };

    const formatYAxis = (value: number): string => {
        const unit = getMetricUnit();
        if (unit === 'BytesPerSecond') {
            return `${(value / 1024).toFixed(1)} KB/s`;
        }
        if (unit === 'Bytes') {
            if (value > 1073741824) {
                return `${(value / 1073741824).toFixed(1)} GB`;
            }
            if (value > 1048576) {
                return `${(value / 1048576).toFixed(1)} MB`;
            }
            if (value > 1024) {
                return `${(value / 1024).toFixed(1)} KB`;
            }
        }
        if (unit === 'Percent') {
            return `${value}%`;
        }
        return value.toString();
    };

    const metricData = getMetricData();
    const availableMetrics = getAvailableMetrics();

    return (
        <div>
            <div className="mb-6 flex flex-wrap">
                {categories.map(category => (
                    <button
                        key={category}
                        className={`mr-2 mb-2 px-4 py-2 rounded-md capitalize ${activeCategory === category
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                        onClick={() => setActiveCategory(category)}
                    >
                        {category}
                    </button>
                ))}
            </div>

            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Select Metric
                </label>
                <select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    className="block w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                    <option value="">Select a metric</option>
                    {availableMetrics.map(metric => (
                        <option key={metric.value} value={metric.value}>
                            {metric.label}
                        </option>
                    ))}
                </select>
            </div>

            {metricData.length > 0 ? (
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={metricData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" />
                            <YAxis tickFormatter={formatYAxis} />
                            <Tooltip
                                formatter={(value: number) => [formatYAxis(value), getMetricUnit()]}
                                labelFormatter={(label: string) => `Time: ${label}`}
                            />
                            <Legend />
                            {metricData[0]?.average !== undefined && (
                                <Line type="monotone" dataKey="average" stroke="#8884d8" name="Average" />
                            )}
                            {metricData[0]?.maximum !== undefined && (
                                <Line type="monotone" dataKey="maximum" stroke="#82ca9d" name="Maximum" />
                            )}
                            {metricData[0]?.minimum !== undefined && (
                                <Line type="monotone" dataKey="minimum" stroke="#ff7300" name="Minimum" />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div className="flex justify-center items-center h-64">
                    <p className="text-gray-500">No data available for this metric</p>
                </div>
            )}
        </div>
    );
};
export default MetricsDisplay;